import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { isNativeCaptureAvailable, launchNativeCapture, getNativeCaptureIntent } from '../extensions/nativeCapture';
import GreenScreenStudio from '../components/GreenScreenStudio';
import EnhancedDAWStudio from '../components/DAWComponents';

const QUALITY_PRESETS = {
  '720p': { label: '720p HD', width: 1280, height: 720 },
  '1080p': { label: '1080p Full HD', width: 1920, height: 1080 },
  '4k': { label: '4K UHD', width: 3840, height: 2160 },
};

const FRAME_RATE_OPTIONS = [24, 30, 60];
const COUNTDOWN_OPTIONS = [0, 3, 5];
const RECORDER_PREF_KEY = 'aiia_recorder_preferences_v2';
const BACKEND_MAX_UPLOAD_BYTES = 500 * 1024 * 1024; // 500MB to match multer limit
const BACKEND_MAX_UPLOAD_LABEL = '500MB';

export function Upload() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Recording states
  const [mode, setMode] = useState('choose'); // 'choose', 'record', 'upload', 'preview', 'audio-studio'
  const [captureMode, setCaptureMode] = useState('video'); // 'video', 'photo'
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [previewMediaKind, setPreviewMediaKind] = useState('video');
  const [recordingTime, setRecordingTime] = useState(0);
  const [maxDuration, setMaxDuration] = useState(30); // 15, 30, 60, or 180 seconds
  const [preferredFacingMode, setPreferredFacingMode] = useState('user');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [resolution, setResolution] = useState('1080p');
  const [frameRate, setFrameRate] = useState(30);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [countdownDuration, setCountdownDuration] = useState(3);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(0);
  const [gridlinesEnabled, setGridlinesEnabled] = useState(true);
  const [cameraStatus, setCameraStatus] = useState('idle');
  const [cameraMetrics, setCameraMetrics] = useState(null);
  const [lastCameraError, setLastCameraError] = useState(null);
  const [controlsCollapsed, setControlsCollapsed] = useState(false);
  const [cameraCapabilities, setCameraCapabilities] = useState(null);
  const [proCameraEnabled, setProCameraEnabled] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [zoomRange, setZoomRange] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(null);
  const [sensorTuningError, setSensorTuningError] = useState(null);
  const [nativeCaptureUri, setNativeCaptureUri] = useState(null);
  const [nativeCaptureMessage, setNativeCaptureMessage] = useState(null);
  const [nativeCaptureError, setNativeCaptureError] = useState(null);
  
  // Video filter states
  const [greenScreenEnabled, setGreenScreenEnabled] = useState(false);
  const [activeFilter, setActiveFilter] = useState('none'); // 'none', 'greenscreen', 'blur', 'beauty'
  const nativeCaptureSupported = useMemo(() => isNativeCaptureAvailable(), []);
  const handleGreenScreenExport = useCallback(({ file: exportedFile, previewUrl, kind = 'video' }) => {
    setFile(exportedFile);
    setRecordedVideo(previewUrl);
    const inferredKind = kind === 'photo' || exportedFile?.type?.startsWith('image/') ? 'photo' : 'video';
    setPreviewMediaKind(inferredKind);
    setMode('preview');
  }, []);
  
  // Upload states
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Cross-media states
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [availableMusic, setAvailableMusic] = useState([]);
  const [showMusicPicker, setShowMusicPicker] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [communities, setCommunities] = useState([]);
  
  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const preferencesLoadedRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/upload', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(RECORDER_PREF_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.resolution) setResolution(parsed.resolution);
        if (parsed.frameRate) setFrameRate(parsed.frameRate);
        if (typeof parsed.audioEnabled === 'boolean') setAudioEnabled(parsed.audioEnabled);
        if (typeof parsed.countdownDuration === 'number') setCountdownDuration(parsed.countdownDuration);
        if (parsed.preferredFacingMode) setPreferredFacingMode(parsed.preferredFacingMode);
        if (parsed.selectedCameraId) setSelectedCameraId(parsed.selectedCameraId);
        if (typeof parsed.gridlinesEnabled === 'boolean') setGridlinesEnabled(parsed.gridlinesEnabled);
        if (typeof parsed.proCameraEnabled === 'boolean') setProCameraEnabled(parsed.proCameraEnabled);
        if (typeof parsed.torchEnabled === 'boolean') setTorchEnabled(parsed.torchEnabled);
        if (typeof parsed.zoomLevel === 'number') setZoomLevel(parsed.zoomLevel);
      }
    } catch (error) {
      console.warn('Unable to load recorder preferences:', error);
    } finally {
      preferencesLoadedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const clipUri = params.get('nativeCapture');
    if (clipUri) {
      setNativeCaptureUri(clipUri);
      setNativeCaptureMessage('Native capture available. Tap "Import native clip" to pull it into the studio.');
      params.delete('nativeCapture');
      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !preferencesLoadedRef.current) return;
    const payload = {
      resolution,
      frameRate,
      audioEnabled,
      countdownDuration,
      preferredFacingMode,
      selectedCameraId,
      gridlinesEnabled,
      proCameraEnabled,
      torchEnabled,
      zoomLevel,
    };
    try {
      window.localStorage.setItem(RECORDER_PREF_KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn('Unable to persist recorder preferences:', error);
    }
  }, [resolution, frameRate, audioEnabled, countdownDuration, preferredFacingMode, selectedCameraId, gridlinesEnabled, proCameraEnabled, torchEnabled, zoomLevel]);

  useEffect(() => {
    const loadDevices = async () => {
      if (!navigator?.mediaDevices?.enumerateDevices) return;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(device => device.kind === 'videoinput');
        setAvailableCameras(cameras);
        if (!selectedCameraId && cameras.length) {
          setSelectedCameraId(cameras[0].deviceId);
        }
      } catch (error) {
        console.warn('Unable to enumerate devices:', error);
      }
    };

    loadDevices();
  }, [selectedCameraId]);

  const requestStream = async (overrides = {}) => {
    const desiredResolution = overrides.resolution || resolution;
    const preset = QUALITY_PRESETS[desiredResolution] || QUALITY_PRESETS['1080p'];
    const desiredFrameRate = overrides.frameRate || frameRate;
    const useAudio = Object.prototype.hasOwnProperty.call(overrides, 'audioEnabled')
      ? overrides.audioEnabled
      : audioEnabled;
    const deviceId = Object.prototype.hasOwnProperty.call(overrides, 'deviceId')
      ? overrides.deviceId
      : selectedCameraId;
    const facingMode = overrides.facingMode || preferredFacingMode;

    const videoConstraints = {
      width: { ideal: preset.width },
      height: { ideal: preset.height },
      frameRate: { ideal: desiredFrameRate },
    };

    if (deviceId) {
      videoConstraints.deviceId = { exact: deviceId };
    } else {
      videoConstraints.facingMode = facingMode;
    }

    return navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
      audio: useAudio,
    });
  };

  const applySensorControls = useCallback(
    async (track, { zoomOverride, torchOverride, forcePro } = {}) => {
      if (!track || typeof track.applyConstraints !== 'function') return;

      const capabilities = track.getCapabilities ? track.getCapabilities() : null;
      if (capabilities) {
        setCameraCapabilities(capabilities);
        if (typeof capabilities.torch === 'boolean') {
          setTorchAvailable(capabilities.torch);
        } else {
          setTorchAvailable(false);
        }

        if (capabilities.zoom) {
          const nextRange = {
            min: capabilities.zoom.min ?? 1,
            max: capabilities.zoom.max ?? capabilities.zoom.min ?? 1,
            step: capabilities.zoom.step ?? 0.1,
          };
          setZoomRange((prev) => {
            if (prev && prev.min === nextRange.min && prev.max === nextRange.max) {
              return prev;
            }
            return nextRange;
          });
          setZoomLevel((prev) => {
            if (prev === null || Number.isNaN(prev)) {
              return capabilities.zoom.default ?? capabilities.zoom.min ?? 1;
            }
            const min = capabilities.zoom.min ?? prev;
            const max = capabilities.zoom.max ?? prev;
            return Math.min(Math.max(prev, min), max);
          });
        } else {
          setZoomRange(null);
        }
      } else {
        setCameraCapabilities(null);
        setTorchAvailable(false);
        setZoomRange(null);
      }

      const advanced = [];
      const shouldUsePro = forcePro ?? proCameraEnabled;

      if (shouldUsePro && capabilities) {
        if (capabilities.focusMode?.includes?.('continuous')) {
          advanced.push({ focusMode: 'continuous' });
        }
        if (capabilities.exposureMode?.includes?.('continuous')) {
          advanced.push({ exposureMode: 'continuous' });
        }
        if (capabilities.whiteBalanceMode?.includes?.('continuous')) {
          advanced.push({ whiteBalanceMode: 'continuous' });
        }
        if (capabilities.frameRate?.max && capabilities.frameRate?.max >= frameRate) {
          advanced.push({ frameRate: Math.min(capabilities.frameRate.max, frameRate) });
        }
      }

      const torchValue = typeof torchOverride === 'boolean' ? torchOverride : torchEnabled;
      if (torchValue && capabilities?.torch) {
        advanced.push({ torch: true });
      }

      const desiredZoom = typeof zoomOverride === 'number' ? zoomOverride : zoomLevel;
      if (typeof desiredZoom === 'number' && capabilities?.zoom) {
        const min = capabilities.zoom.min ?? desiredZoom;
        const max = capabilities.zoom.max ?? desiredZoom;
        const nextZoom = Math.min(Math.max(desiredZoom, min), max);
        advanced.push({ zoom: nextZoom });
      }

      if (!advanced.length) {
        setSensorTuningError(null);
        return;
      }

      try {
        await track.applyConstraints({ advanced });
        setSensorTuningError(null);
      } catch (error) {
        console.warn('Unable to apply camera constraints:', error);
        setSensorTuningError(error?.message || 'Low-level camera controls are not supported on this device.');
      }
    },
    [frameRate, proCameraEnabled, torchEnabled, zoomLevel]
  );

  const refreshCameraStream = async (overrides = {}) => {
    setCameraStatus('initializing');
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      const stream = await requestStream(overrides);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        setCameraMetrics({
          width: settings.width,
          height: settings.height,
          frameRate: settings.frameRate,
          deviceId: settings.deviceId,
          facingMode: settings.facingMode,
        });
        await applySensorControls(videoTrack);
      } else {
        setCameraMetrics(null);
      }
      setCameraStatus('ready');
      setLastCameraError(null);
      return stream;
    } catch (error) {
      console.error('Unable to refresh camera stream:', error);
      setCameraStatus('error');
      setLastCameraError(error?.message || 'Camera access denied');
      throw error;
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      await refreshCameraStream();
      setIsCountingDown(false);
      setCountdownValue(0);
      setControlsCollapsed(false);
      setMode('record');
    } catch (error) {
      console.error('Camera access denied:', error);
      setCameraStatus('error');
      setLastCameraError(error?.message || 'Camera access is required');
      alert('Camera access is required to record videos. Please enable camera permissions.');
    }
  };

  // Flip camera
  const flipCamera = async () => {
    if (!streamRef.current) return;
    const newFacingMode = preferredFacingMode === 'user' ? 'environment' : 'user';
    setPreferredFacingMode(newFacingMode);
    setSelectedCameraId(null);
    try {
      await refreshCameraStream({ facingMode: newFacingMode });
    } catch (error) {
      alert('Unable to switch cameras. Please ensure permissions are enabled.');
    }
  };

  // Start recording
  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    
    // Mirror if user facing
    if (preferredFacingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    
    ctx.drawImage(videoRef.current, 0, 0);
    
    canvas.toBlob((blob) => {
      const filename = `photo-${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });
      setFile(file);
      setRecordedVideo(URL.createObjectURL(blob));
      setPreviewMediaKind('photo');
      setMode('preview');
      
      // Stop camera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      setCameraStatus('idle');
    }, 'image/jpeg', 0.9);
  };

  const startRecordingNow = () => {
    if (!streamRef.current) return;
    
    chunksRef.current = [];
    const bitrateMap = {
      '720p': 2500000,
      '1080p': 5000000,
      '4k': 8000000,
    };
    const videoBitsPerSecond = bitrateMap[resolution] || 3500000;
    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond,
    });
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const file = new File([blob], `recording-${Date.now()}.webm`, { type: 'video/webm' });
      setRecordedVideo(URL.createObjectURL(blob));
    setPreviewMediaKind('video');
      setFile(file);
      setMode('preview');
      
      // Stop camera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      setCameraStatus('idle');
      setCameraMetrics(null);
    };
    
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setIsRecording(true);
    setRecordingTime(0);
    setIsCountingDown(false);
    setCountdownValue(0);
    
    // Start timer
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const newTime = prev + 1;
        if (newTime >= maxDuration) {
          stopRecording();
          return maxDuration;
        }
        return newTime;
      });
    }, 1000);
  };

  const startRecording = () => {
    if (isRecording || isCountingDown) return;
    setControlsCollapsed(true);
    if (countdownDuration <= 0) {
      startRecordingNow();
      return;
    }

    setCountdownValue(countdownDuration);
    setIsCountingDown(true);
    countdownTimerRef.current = setInterval(() => {
      setCountdownValue(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          startRecordingNow();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setIsCountingDown(false);
    setCountdownValue(0);
    setControlsCollapsed(false);
  };

  const handleCameraSelection = async (deviceId) => {
    setSelectedCameraId(deviceId || null);
    if (mode === 'record' && streamRef.current) {
      try {
        await refreshCameraStream({ deviceId });
      } catch (error) {
        alert('Camera selection failed. Please try again.');
      }
    }
  };

  const handleResolutionChange = async (value) => {
    setResolution(value);
    if (mode === 'record' && streamRef.current) {
      try {
        await refreshCameraStream({ resolution: value });
      } catch (error) {
        alert('Unable to apply the selected resolution.');
      }
    }
  };

  const handleFrameRateChange = async (value) => {
    setFrameRate(value);
    if (mode === 'record' && streamRef.current) {
      try {
        await refreshCameraStream({ frameRate: value });
      } catch (error) {
        alert('Unable to update frame rate.');
      }
    }
  };

  const handleAudioToggle = async () => {
    const next = !audioEnabled;
    setAudioEnabled(next);
    if (mode === 'record' && streamRef.current) {
      try {
        await refreshCameraStream({ audioEnabled: next });
      } catch (error) {
        alert('Unable to toggle audio input.');
      }
    }
  };

  const handleCountdownChange = (value) => {
    setCountdownDuration(value);
  };

  const restartCamera = async () => {
    try {
      await refreshCameraStream();
    } catch (error) {
      alert('Unable to restart the camera. Please check permissions.');
    }
  };

  const toggleGridlines = () => {
    setGridlinesEnabled((prev) => !prev);
  };

  const toggleProCameraControls = () => {
    setProCameraEnabled((prev) => !prev);
    setSensorTuningError(null);
  };

  const handleTorchToggle = () => {
    if (!torchAvailable) return;
    setTorchEnabled((prev) => !prev);
  };

  const importNativeCapture = async () => {
    if (!nativeCaptureUri) return;
    setNativeCaptureError(null);
    setNativeCaptureMessage('Importing native clip…');
    try {
      const response = await fetch(nativeCaptureUri);
      if (!response.ok) {
        throw new Error('Native clip is not accessible yet. Reopen from the native recorder.');
      }
      const blob = await response.blob();
      const inferredType = blob.type || 'video/mp4';
      const filename = `native-${Date.now()}.mp4`;
      const nativeFile = new File([blob], filename, { type: inferredType });
      setFile(nativeFile);
      setRecordedVideo(URL.createObjectURL(blob));
  setPreviewMediaKind('video');
      setMode('preview');
      setNativeCaptureMessage('Native clip imported. Review and publish below.');
      setNativeCaptureUri(null);
    } catch (error) {
      console.error('Unable to import native clip:', error);
      setNativeCaptureError(error.message || 'Import failed. Please retry from the native recorder.');
      setNativeCaptureMessage(null);
    }
  };

  // Retake video
  const retakeVideo = () => {
    setRecordedVideo(null);
    setPreviewMediaKind('video');
    setFile(null);
    setRecordingTime(0);
    setIsCountingDown(false);
    setCountdownValue(0);
    setSelectedMusic(null);
    setSelectedCommunity(null);
    startCamera();
  };
  
  // Load available music for cross-media
  const loadAvailableMusic = async () => {
    try {
      const response = await apiService.getAvailableMusic({ limit: 50 });
      setAvailableMusic(response.tracks || []);
      setShowMusicPicker(true);
    } catch (err) {
      console.error('Failed to load music:', err);
    }
  };
  
  // Load user's communities
  const loadCommunities = async () => {
    try {
      const response = await apiService.getUserCommunities();
      setCommunities(response.communities || []);
    } catch (err) {
      console.error('Failed to load communities:', err);
    }
  };
  
  // Load communities when entering preview mode
  useEffect(() => {
    if (mode === 'preview') {
      loadCommunities();
    }
  }, [mode]);

  useEffect(() => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    applySensorControls(track);
  }, [applySensorControls]);

  const zoomSliderStep = useMemo(() => {
    if (!zoomRange) return 0.1;
    if (zoomRange.step) return zoomRange.step;
    const span = zoomRange.max - zoomRange.min;
    if (span <= 0) return 0.1;
    return Math.max(0.1, span / 20);
  }, [zoomRange]);

  // File upload handler
  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      const isVideo = selected.type.startsWith('video/');
      const isImage = selected.type.startsWith('image/');
      
      if (!isVideo && !isImage) {
        alert('Please select a video or image file');
        return;
      }
      if (selected.size > BACKEND_MAX_UPLOAD_BYTES) {
        const sizeMb = (selected.size / (1024 * 1024)).toFixed(1);
        alert(`This file is ${sizeMb}MB. The current pipeline accepts uploads up to ${BACKEND_MAX_UPLOAD_LABEL}. Please trim or compress before uploading.`);
        return;
      }
      setFile(selected);
      setRecordedVideo(URL.createObjectURL(selected));
      setPreviewMediaKind(isVideo ? 'video' : 'photo');
      setMode('preview');
    }
  };

  // Submit video
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    setProgress(0);
    let progressInterval;

    try {
      const formData = new FormData();
      formData.append('video', file);
      formData.append('caption', caption);
      formData.append('hashtags', hashtags);
      formData.append('visibility', visibility);
      
      // Add cross-media data
      if (selectedMusic) {
        formData.append('background_audio_id', selectedMusic.id);
      }
      if (selectedCommunity) {
        formData.append('community_id', selectedCommunity.id);
      }
      formData.append('feed_type', 'general');
      formData.append('content_type', previewMediaKind === 'photo' ? 'image' : 'video');

      progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 300);

  await apiService.createPost(formData);
      
      // If music was used, record the usage
      if (selectedMusic) {
        try {
          // Track will be incremented server-side
          console.log('Track usage recorded:', selectedMusic.title);
        } catch (err) {
          console.warn('Failed to record track usage:', err);
        }
      }
      
      setProgress(100);

      alert('Posted successfully! 🎉');
      navigate('/');
    } catch (error) {
      console.error('Upload failed:', error);
      const friendly = error?.message === 'Failed to fetch'
        ? `Upload failed before reaching the server. Please confirm your connection and keep files under ${BACKEND_MAX_UPLOAD_LABEL}.`
        : error?.message || 'Unknown error';
      alert(`Upload failed: ${friendly}`);
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <div className="h-screen w-full bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <p className="text-2xl font-semibold mb-3">Sign in required</p>
        <p className="text-gray-400 max-w-md">
          You need an active profile to upload or record videos. Redirecting you to the login page…
        </p>
      </div>
    );
  }

  // ========================================
  // RENDER: Choose Mode
  // ========================================
  if (mode === 'choose') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-start px-4 pt-8 pb-24 overflow-y-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Create</h1>
          <p className="text-gray-400 text-sm md:text-base">Record video, create audio, art, stories, or upload content</p>
        </div>

        <div className="w-full max-w-md space-y-3">
          {/* Record Video Button - Now includes green screen as filter */}
          <button
            onClick={startCamera}
            className="w-full py-5 px-6 rounded-2xl bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-bold text-base md:text-lg flex items-center justify-between group transition-all hover:scale-[1.02]"
          >
            <span className="flex items-center gap-3">
              <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                <circle cx="10" cy="10" r="8" />
              </svg>
              <span>
                <span className="block">Record Video</span>
                <span className="text-xs font-normal opacity-80">Includes filters & green screen</span>
              </span>
            </span>
            <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Audio Studio / DAW Button */}
          <button
            onClick={() => setMode('audio-studio')}
            className="w-full py-5 px-6 rounded-2xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-bold text-base md:text-lg flex items-center justify-between group transition-all hover:scale-[1.02]"
          >
            <span className="flex items-center gap-3">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
              <span>
                <span className="block">Audio Studio</span>
                <span className="text-xs font-normal opacity-80">DAW, beats, effects & mixing</span>
              </span>
            </span>
            <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Art Studio Button */}
          <button
            onClick={() => navigate('/art-studio')}
            className="w-full py-5 px-6 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold text-base md:text-lg flex items-center justify-between group transition-all hover:scale-[1.02]"
          >
            <span className="flex items-center gap-3">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>
                <span className="block">Art Studio</span>
                <span className="text-xs font-normal opacity-80">Digital canvas, brushes & layers</span>
              </span>
            </span>
            <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Writer's Studio Button */}
          <button
            onClick={() => navigate('/stories/write')}
            className="w-full py-5 px-6 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-base md:text-lg flex items-center justify-between group transition-all hover:scale-[1.02]"
          >
            <span className="flex items-center gap-3">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span>
                <span className="block">Writer's Studio</span>
                <span className="text-xs font-normal opacity-80">Stories, fan fiction & poetry</span>
              </span>
            </span>
            <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Upload Button */}
          <label className="block w-full py-5 px-6 rounded-2xl bg-white/10 hover:bg-white/15 text-white font-bold text-base md:text-lg cursor-pointer border-2 border-white/20 transition-all hover:scale-[1.02]">
            <input
              type="file"
              accept="video/*,audio/*,image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <span className="flex items-center justify-between">
              <span className="flex items-center gap-3">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>
                  <span className="block">Upload Media</span>
                  <span className="text-xs font-normal opacity-80">Video, audio, or images</span>
                </span>
              </span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </label>
          <p className="text-xs text-center text-gray-500">Limit {BACKEND_MAX_UPLOAD_LABEL} per file</p>

          <button
            onClick={() => setMode('choose')}
            className="w-full py-3 text-gray-400 hover:text-white text-sm transition-colors"
          >
            Cancel
          </button>

          {nativeCaptureSupported && (
            <button
              onClick={() => launchNativeCapture()}
              className="w-full py-4 px-6 rounded-2xl bg-green-500/20 hover:bg-green-500/30 border border-green-400/40 text-green-100 font-semibold transition-all flex items-center justify-between"
            >
              <span className="flex items-center gap-3">
                <span className="text-2xl">📱</span>
                <span className="text-sm">Launch Native Capture (Android)</span>
              </span>
              <span className="text-xs bg-green-500/30 px-2 py-1 rounded-full">PRO</span>
            </button>
          )}

          {nativeCaptureUri && (
            <div className="w-full rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-4 text-left text-white/90 space-y-3">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-emerald-200">Native clip ready</p>
                <p className="text-sm text-white/80 break-all">{nativeCaptureUri}</p>
              </div>
              {nativeCaptureMessage && (
                <p className="text-xs text-emerald-100">{nativeCaptureMessage}</p>
              )}
              {nativeCaptureError && (
                <p className="text-xs text-rose-200">{nativeCaptureError}</p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={importNativeCapture}
                  className="px-4 py-2 rounded-full bg-emerald-500 text-black font-semibold"
                >
                  Import native clip
                </button>
                <a
                  href={getNativeCaptureIntent()}
                  className="px-4 py-2 rounded-full border border-white/20 text-white/80"
                >
                  Re-open recorder
                </a>
              </div>
            </div>
          )}

          <button
            onClick={() => navigate('/')}
            className="w-full py-4 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER: Audio Studio / DAW
  // ========================================
  if (mode === 'audio-studio') {
    return (
      <div className="bg-slate-950 flex flex-col pt-20 pb-24 h-full">
        
        {/* DAW Component */}
        <div className="flex-1 overflow-hidden">
          <EnhancedDAWStudio 
            onBack={() => setMode('choose')}
            onExport={(audioFile) => {
              // Handle exported audio
              setFile(audioFile);
              setPreviewMediaKind('audio');
              setMode('preview');
            }}
          />
        </div>
      </div>
    );
  }

  if (mode === 'green-screen') {
    return (
      <GreenScreenStudio
        onExport={handleGreenScreenExport}
        onBack={() => setMode('choose')}
      />
    );
  }

  // ========================================
  // RENDER: Recording Mode
  // ========================================
  if (mode === 'record') {
    const cameraStatusTokens = {
      initializing: {
        label: 'Initializing',
        className: 'border-amber-400/40 bg-amber-500/15 text-amber-100',
      },
      ready: {
        label: 'Live',
        className: 'border-emerald-400/50 bg-emerald-500/15 text-emerald-100',
      },
      error: {
        label: 'Offline',
        className: 'border-rose-400/50 bg-rose-500/15 text-rose-100',
      },
      idle: {
        label: 'Idle',
        className: 'border-white/30 bg-white/10 text-white/70',
      },
    };
    const statusMeta = cameraStatusTokens[cameraStatus] || cameraStatusTokens.idle;
  const selectedResolutionLabel = QUALITY_PRESETS[resolution]?.label || 'Custom';

    return (
      <div className="h-screen bg-black relative overflow-hidden">
        {/* Camera Preview */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover bg-black"
        />

        {/* Composition guides */}
        {gridlinesEnabled && (
          <div className="absolute inset-0 pointer-events-none z-5">
            <div className="absolute inset-[8%] border border-white/15 rounded-[32px]" />
            <div className="absolute top-1/3 left-[8%] right-[8%] border-t border-white/10" />
            <div className="absolute top-2/3 left-[8%] right-[8%] border-t border-white/10" />
            <div className="absolute left-1/3 top-[8%] bottom-[8%] border-l border-white/10" />
            <div className="absolute left-2/3 top-[8%] bottom-[8%] border-l border-white/10" />
          </div>
        )}

        {/* Countdown overlay */}
  {isCountingDown && countdownValue > 0 && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="w-28 h-28 rounded-full bg-black/60 border-2 border-white/40 flex items-center justify-center">
              <span className="text-5xl font-bold tracking-widest">
                {countdownValue}
              </span>
            </div>
          </div>
        )}

        {/* Top Bar */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/60 to-transparent z-10">
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                if (streamRef.current) {
                  streamRef.current.getTracks().forEach(track => track.stop());
                }
                setCameraStatus('idle');
                setCameraMetrics(null);
                setLastCameraError(null);
                if (countdownTimerRef.current) {
                  clearInterval(countdownTimerRef.current);
                  countdownTimerRef.current = null;
                }
                setIsCountingDown(false);
                setCountdownValue(0);
                setMode('choose');
              }}
              className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center hover:bg-black/60"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Time Limit Selector */}
            <div className="flex gap-2 bg-black/40 rounded-full p-1">
              {[15, 30, 60, 180].map(duration => (
                <button
                  key={duration}
                  onClick={() => setMaxDuration(duration)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    maxDuration === duration
                      ? 'bg-white text-black'
                      : 'text-white hover:bg-white/20'
                  }`}
                  disabled={isRecording}
                >
                  {duration < 60 ? `${duration}s` : `${duration / 60}m`}
                </button>
              ))}
            </div>

            <button
              onClick={flipCamera}
              disabled={isRecording}
              className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center hover:bg-black/60 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Right Side Filter Bar */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-3">
          {/* Filter Toggle Buttons */}
          <div className="bg-black/60 backdrop-blur rounded-2xl p-2 space-y-2">
            <button
              onClick={() => setActiveFilter(activeFilter === 'greenscreen' ? 'none' : 'greenscreen')}
              disabled={isRecording}
              className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center transition-all ${
                activeFilter === 'greenscreen'
                  ? 'bg-green-500 text-black'
                  : 'bg-white/10 text-white hover:bg-white/20'
              } ${isRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Green Screen"
            >
              <span className="text-lg">🟢</span>
              <span className="text-[8px] font-bold">BG</span>
            </button>

            <button
              onClick={() => setActiveFilter(activeFilter === 'blur' ? 'none' : 'blur')}
              disabled={isRecording}
              className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center transition-all ${
                activeFilter === 'blur'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/10 text-white hover:bg-white/20'
              } ${isRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Background Blur"
            >
              <span className="text-lg">🌫️</span>
              <span className="text-[8px] font-bold">BLUR</span>
            </button>

            <button
              onClick={() => setActiveFilter(activeFilter === 'beauty' ? 'none' : 'beauty')}
              disabled={isRecording}
              className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center transition-all ${
                activeFilter === 'beauty'
                  ? 'bg-pink-500 text-white'
                  : 'bg-white/10 text-white hover:bg-white/20'
              } ${isRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Beauty Mode"
            >
              <span className="text-lg">✨</span>
              <span className="text-[8px] font-bold">BEAUTY</span>
            </button>
          </div>

          {/* Active Filter Indicator */}
          {activeFilter !== 'none' && (
            <div className="bg-black/60 backdrop-blur rounded-xl px-2 py-1 text-center">
              <span className="text-xs text-white/80 font-medium capitalize">{activeFilter}</span>
            </div>
          )}
        </div>

        {/* Green Screen Settings Panel (shows when filter is active) */}
        {activeFilter === 'greenscreen' && !isRecording && (
          <div className="absolute left-4 top-20 z-10 bg-black/80 backdrop-blur-xl rounded-2xl p-4 w-64 border border-white/10">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-white">Green Screen</h3>
              <button
                onClick={() => setActiveFilter('none')}
                className="text-white/60 hover:text-white"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-white/60 mb-3">
              Replace your background with a solid color or image
            </p>
            <div className="space-y-2">
              <button className="w-full py-2 px-3 rounded-lg bg-green-500/20 text-green-300 text-sm font-medium hover:bg-green-500/30 transition-colors text-left flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-green-500"></span>
                Chroma Green
              </button>
              <button className="w-full py-2 px-3 rounded-lg bg-blue-500/20 text-blue-300 text-sm font-medium hover:bg-blue-500/30 transition-colors text-left flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-blue-500"></span>
                Chroma Blue
              </button>
              <label className="w-full py-2 px-3 rounded-lg bg-white/10 text-white/80 text-sm font-medium hover:bg-white/20 transition-colors cursor-pointer flex items-center gap-2">
                <span className="text-lg">🖼️</span>
                Upload Image
                <input type="file" accept="image/*" className="hidden" />
              </label>
            </div>
          </div>
        )}

        {/* Recording Timer */}
        {isRecording && (
          <div className="absolute top-20 left-0 right-0 flex justify-center z-10">
            <div className="bg-red-500 text-white px-4 py-2 rounded-full flex items-center gap-2 animate-pulse">
              <div className="w-3 h-3 bg-white rounded-full"></div>
              <span className="font-mono font-bold">
                {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')} / {Math.floor(maxDuration / 60)}:{(maxDuration % 60).toString().padStart(2, '0')}
              </span>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {isRecording && (
          <div className="absolute top-32 left-4 right-4 h-1 bg-white/20 rounded-full overflow-hidden z-10">
            <div 
              className="h-full bg-red-500 transition-all duration-1000"
              style={{ width: `${(recordingTime / maxDuration) * 100}%` }}
            />
          </div>
        )}

        {/* Advanced controls */}
        <div className="absolute bottom-44 left-0 right-0 px-6 z-10">
          <div className="max-w-3xl mx-auto space-y-2">
            <div className={`bg-black/60 backdrop-blur-xl border border-white/10 rounded-3xl transition-all ${
              controlsCollapsed ? 'p-3 sm:p-4' : 'p-4 sm:p-5'
            }`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-white/60">Recording Controls</p>
                  <p className="text-sm text-white/70">
                    {controlsCollapsed
                      ? `${selectedResolutionLabel} • ${frameRate}fps • ${audioEnabled ? 'Audio on' : 'Muted'} • Countdown ${
                          countdownDuration ? `${countdownDuration}s` : 'Off'
                        }`
                      : 'Adjust camera quality, audio, countdown, and tools.'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={toggleGridlines}
                    className={`hidden sm:inline-flex px-3 py-2 rounded-full text-xs font-semibold border transition-all ${
                      gridlinesEnabled
                        ? 'border-white/50 text-white bg-white/10'
                        : 'border-white/30 text-white/70 hover:bg-white/10'
                    }`}
                  >
                    {gridlinesEnabled ? 'Hide Guides' : 'Show Guides'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setControlsCollapsed((prev) => !prev)}
                    className="px-4 py-2 rounded-full text-sm font-semibold border border-white/30 text-white/80 hover:bg-white/10"
                  >
                    {controlsCollapsed ? 'Show Controls' : 'Minimize' }
                  </button>
                </div>
              </div>

              {!controlsCollapsed && (
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-white/60 mb-2">Resolution</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(QUALITY_PRESETS).map(([key, value]) => (
                          <button
                            key={key}
                            onClick={() => handleResolutionChange(key)}
                            disabled={isRecording}
                            className={`px-3 py-2 rounded-full text-sm font-semibold transition-all border ${
                              resolution === key
                                ? 'bg-white text-black border-white'
                                : 'border-white/20 text-white/80 hover:border-white/60'
                            } ${isRecording ? 'opacity-70 cursor-not-allowed' : ''}`}
                          >
                            {value.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-wide text-white/60 mb-2">Frame Rate</p>
                      <div className="flex gap-2">
                        {FRAME_RATE_OPTIONS.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleFrameRateChange(option)}
                            disabled={isRecording}
                            className={`px-3 py-2 rounded-full text-sm font-semibold transition-all border ${
                              frameRate === option
                                ? 'bg-white text-black border-white'
                                : 'border-white/20 text-white/80 hover:border-white/60'
                            } ${isRecording ? 'opacity-70 cursor-not-allowed' : ''}`}
                          >
                            {option}fps
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-white/60 mb-2">Countdown</p>
                      <div className="flex gap-2">
                        {COUNTDOWN_OPTIONS.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleCountdownChange(option)}
                            className={`px-3 py-2 rounded-full text-sm font-semibold transition-all border ${
                              countdownDuration === option
                                ? 'bg-white text-black border-white'
                                : 'border-white/20 text-white/80 hover:border-white/60'
                            }`}
                          >
                            {option === 0 ? 'Off' : `${option}s`}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-white/60 mb-1">Audio Input</p>
                        <p className="text-sm text-white/70">{audioEnabled ? 'Microphone enabled' : 'Muted'}</p>
                      </div>
                      <button
                        onClick={handleAudioToggle}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                          audioEnabled
                            ? 'border-green-400 text-green-300 bg-green-400/10'
                            : 'border-white/30 text-white/70 hover:bg-white/10'
                        }`}
                      >
                        {audioEnabled ? 'Mute Audio' : 'Enable Audio'}
                      </button>
                    </div>
                  </div>

                  {availableCameras.length > 1 && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-white/60 mb-2">Camera Selection</p>
                      <select
                        value={selectedCameraId || ''}
                        onChange={(e) => handleCameraSelection(e.target.value || null)}
                        className="w-full bg-black/40 border border-white/20 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                      >
                        <option value="">Auto ({preferredFacingMode === 'user' ? 'Front' : 'Rear'})</option>
                        {availableCameras.map((camera, index) => (
                          <option key={camera.deviceId} value={camera.deviceId}>
                            {camera.label || `Camera ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="rounded-2xl border border-white/10 p-4 space-y-3 bg-black/30">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-white/60">Low-level capture</p>
                        <p className="text-sm text-white/70">
                          Bypass the browser fallback by locking directly to the device sensor for sharper footage.
                        </p>
                        {cameraCapabilities && (
                          <p className="text-xs text-white/50 mt-1">
                            Direct controls: {[
                              cameraCapabilities.focusMode ? 'Focus' : null,
                              cameraCapabilities.exposureMode ? 'Exposure' : null,
                              cameraCapabilities.whiteBalanceMode ? 'White balance' : null,
                              cameraCapabilities.zoom ? 'Zoom' : null,
                              cameraCapabilities.torch ? 'Torch' : null,
                            ].filter(Boolean).join(' • ') || 'Auto only'}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={toggleProCameraControls}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                          proCameraEnabled
                            ? 'border-emerald-400 text-emerald-200 bg-emerald-400/10'
                            : 'border-white/30 text-white/70 hover:bg-white/10'
                        }`}
                      >
                        {proCameraEnabled ? 'Disable Pro Mode' : 'Enable Pro Mode'}
                      </button>
                    </div>
                    {sensorTuningError && (
                      <p className="text-xs text-rose-300">⚠ {sensorTuningError}</p>
                    )}
                    {proCameraEnabled && (
                      <div className="space-y-4">
                        {zoomRange && (
                          <label className="flex flex-col gap-2 text-sm text-white/70">
                            <span className="flex items-center justify-between text-xs uppercase tracking-wide text-white/60">
                              Optical zoom
                              <span className="text-white">{(zoomLevel ?? zoomRange.min)?.toFixed?.(1)}x</span>
                            </span>
                            <input
                              type="range"
                              min={zoomRange.min}
                              max={zoomRange.max}
                              step={zoomSliderStep}
                              value={zoomLevel ?? zoomRange.min}
                              onChange={(e) => setZoomLevel(parseFloat(e.target.value))}
                              className="w-full accent-white"
                            />
                          </label>
                        )}
                        {torchAvailable && (
                          <button
                            type="button"
                            onClick={handleTorchToggle}
                            className={`w-full px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                              torchEnabled
                                ? 'border-amber-400 text-amber-200 bg-amber-400/10'
                                : 'border-white/30 text-white/70 hover:bg-white/10'
                            }`}
                          >
                            {torchEnabled ? 'Disable Torch' : 'Enable Torch'}
                          </button>
                        )}
                        {!zoomRange && !torchAvailable && (
                          <p className="text-xs text-white/60">
                            Device does not expose manual zoom or torch controls, but focus/exposure overrides remain active.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-white/10 pt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-1 text-white/80 text-sm">
                      <p className="text-xs uppercase tracking-wide text-white/60">Camera Status</p>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                        {cameraMetrics && (
                          <span className="px-3 py-1 rounded-full bg-white/10 text-xs font-mono">
                            {(cameraMetrics.width || '–')}×{(cameraMetrics.height || '–')} @
                            {(cameraMetrics.frameRate || frameRate || 30)}fps
                          </span>
                        )}
                      </div>
                      {lastCameraError && cameraStatus === 'error' && (
                        <p className="text-rose-300 text-xs">⚠️ {lastCameraError}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={toggleGridlines}
                        className={`sm:hidden px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                          gridlinesEnabled
                            ? 'border-white/50 text-white bg-white/10'
                            : 'border-white/30 text-white/70 hover:bg-white/10'
                        }`}
                      >
                        {gridlinesEnabled ? 'Hide Guides' : 'Show Guides'}
                      </button>
                      <button
                        type="button"
                        onClick={restartCamera}
                        disabled={cameraStatus === 'initializing'}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all flex items-center gap-2 ${
                          cameraStatus === 'initializing'
                            ? 'border-white/20 text-white/50 cursor-not-allowed'
                            : 'border-white/40 text-white hover:bg-white/10'
                        }`}
                      >
                        <span className="text-base">↻</span>
                        Restart Camera
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/60 to-transparent z-10">
          {/* Mode Toggle */}
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={() => setCaptureMode('video')}
              disabled={isRecording}
              className={`px-4 py-1 rounded-full text-sm font-semibold transition-all ${
                captureMode === 'video' ? 'bg-white text-black' : 'text-white/70 hover:text-white'
              }`}
            >
              Video
            </button>
            <button
              onClick={() => setCaptureMode('photo')}
              disabled={isRecording}
              className={`px-4 py-1 rounded-full text-sm font-semibold transition-all ${
                captureMode === 'photo' ? 'bg-white text-black' : 'text-white/70 hover:text-white'
              }`}
            >
              Photo
            </button>
          </div>

          <div className="flex items-center justify-center">
            {captureMode === 'video' ? (
              !isRecording ? (
                <button
                  onClick={startRecording}
                  disabled={isCountingDown}
                  className={`w-20 h-20 rounded-full border-4 flex items-center justify-center transition-all hover:scale-110 ${
                    isCountingDown
                      ? 'bg-red-500/50 border-white/40 cursor-not-allowed'
                      : 'bg-red-500 border-white hover:bg-red-600'
                  }`}
                >
                  <div className="w-16 h-16 rounded-full bg-red-600" />
                </button>
              ) : (
                <button
                  onClick={stopRecording}
                  className="w-20 h-20 rounded-full bg-white border-4 border-red-500 flex items-center justify-center hover:bg-gray-100 transition-all hover:scale-110"
                >
                  <div className="w-8 h-8 bg-red-500 rounded-sm"></div>
                </button>
              )
            ) : (
              <button
                onClick={capturePhoto}
                className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center transition-all hover:scale-110 bg-white/20 hover:bg-white/30"
              >
                <div className="w-16 h-16 rounded-full bg-white" />
              </button>
            )}
          </div>
          
          <p className="text-center text-white/80 text-sm mt-4">
            {captureMode === 'video'
              ? (isRecording
                ? 'Tap to stop recording'
                : isCountingDown
                ? 'Hold steady... starting in'
                : 'Tap to start recording')
              : 'Tap to take photo'}
          </p>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER: Preview & Publish
  // ========================================
  if (mode === 'preview') {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-2xl font-bold">Preview & Publish</h1>
            <button
              onClick={() => {
                setMode('choose');
                setFile(null);
                setRecordedVideo(null);
                setPreviewMediaKind('video');
              }}
              className="text-gray-400 hover:text-white"
            >
              Cancel
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Video Preview */}
            <div className="rounded-3xl overflow-hidden bg-gray-900 aspect-[9/16] flex items-center justify-center relative">
              {recordedVideo ? (
                previewMediaKind === 'photo' ? (
                  <img
                    src={recordedVideo}
                    alt="Green screen capture"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <video
                    src={recordedVideo}
                    controls
                    className="w-full h-full object-contain"
                  />
                )
              ) : (
                <div className="text-gray-500">No capture yet</div>
              )}

              {recordedVideo && (
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold bg-black/70 text-white/80 border border-white/10">
                  {previewMediaKind === 'photo' ? 'Photo capture' : 'Video capture'}
                </span>
              )}
            </div>

            {/* Post Details Form */}
            <div className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Caption */}
                <div>
                  <label className="block text-sm font-medium mb-2">Caption</label>
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={4}
                    placeholder="Tell us about your video..."
                    required
                  />
                </div>

                {/* Hashtags */}
                <div>
                  <label className="block text-sm font-medium mb-2">Hashtags</label>
                  <input
                    type="text"
                    value={hashtags}
                    onChange={(e) => setHashtags(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#ai #video #content"
                  />
                </div>

                {/* Visibility */}
                <div>
                  <label className="block text-sm font-medium mb-2">Visibility</label>
                  <select
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="public">🌍 Public - Everyone</option>
                    <option value="followers">👥 Followers Only</option>
                    <option value="subscribers">⭐ Subscribers Only</option>
                    <option value="draft">📝 Draft - Private</option>
                  </select>
                </div>

                {/* Cross-Media Options */}
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <label className="block text-sm font-medium mb-3">Enhance Your Post</label>
                  
                  {/* Add Background Music */}
                  <button
                    type="button"
                    onClick={loadAvailableMusic}
                    className={`w-full px-4 py-3 rounded-lg mb-2 flex items-center justify-between transition ${
                      selectedMusic 
                        ? 'bg-purple-500/30 border border-purple-500/50' 
                        : 'bg-white/5 hover:bg-white/10 border border-white/10'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span>🎵</span>
                      <span className="text-sm">
                        {selectedMusic ? selectedMusic.title : 'Add Background Music'}
                      </span>
                    </span>
                    {selectedMusic && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSelectedMusic(null); }}
                        className="text-gray-400 hover:text-white"
                      >
                        ✕
                      </button>
                    )}
                  </button>
                  
                  {/* Community Selection */}
                  {communities.length > 0 && (
                    <select
                      value={selectedCommunity?.id || ''}
                      onChange={(e) => {
                        const community = communities.find(c => c.id === parseInt(e.target.value));
                        setSelectedCommunity(community || null);
                      }}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none text-sm"
                    >
                      <option value="">Post to your profile</option>
                      {communities.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Buttons */}
                <div className="space-y-3">
                  {file && (
                    <button
                      onClick={retakeVideo}
                      type="button"
                      className="w-full py-3 rounded-full bg-white/10 hover:bg-white/15 font-semibold border border-white/20"
                    >
                      Retake Video
                    </button>
                  )}
                  
                  <button
                    type="submit"
                    disabled={!file || uploading}
                    className="w-full py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 font-semibold text-white hover:shadow-lg hover:shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? `Publishing... ${progress}%` : 'Publish Video 🚀'}
                  </button>
                  
                  {/* Video Boost Option */}
                  <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🚀</span>
                        <span className="font-medium text-amber-400">Boost Your Video</span>
                      </div>
                      <span className="text-xs text-gray-400">Creator Pro</span>
                    </div>
                    <p className="text-xs text-gray-400 mb-3">
                      Get more views by promoting your video to a wider audience
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/video-boost', { state: { pendingUpload: true } })}
                      className="w-full py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-amber-500/30 transition"
                    >
                      Set Up Boost After Publishing
                    </button>
                  </div>
                </div>

                {uploading && (
                  <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
        
        {/* Music Picker Modal */}
        {showMusicPicker && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
            <div className="bg-slate-900 rounded-xl p-4 w-full max-w-md max-h-[70vh] overflow-y-auto border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">Select Background Music</h3>
                <button onClick={() => setShowMusicPicker(false)} className="p-2 hover:bg-white/10 rounded">×</button>
              </div>
              <div className="space-y-2">
                {availableMusic.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No music available yet. Create some in the Audio Studio!</p>
                ) : (
                  availableMusic.map((track) => (
                    <button
                      key={track.id}
                      onClick={() => {
                        setSelectedMusic(track);
                        setShowMusicPicker(false);
                      }}
                      className={`w-full p-3 rounded-lg text-left flex items-center gap-3 transition-colors ${
                        selectedMusic?.id === track.id 
                          ? 'bg-purple-500/30 border border-purple-500' 
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                      }`}
                    >
                      <div className="w-10 h-10 bg-purple-500/20 rounded flex items-center justify-center text-lg">
                        🎵
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{track.title}</p>
                        <p className="text-xs text-gray-400">by {track.username || 'Unknown'}</p>
                      </div>
                      {track.duration_ms && (
                        <span className="text-xs text-gray-400">
                          {Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setSelectedMusic(null);
                    setShowMusicPicker(false);
                  }}
                  className="flex-1 px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20"
                >
                  Clear Selection
                </button>
                <button
                  onClick={() => setShowMusicPicker(false)}
                  className="flex-1 px-4 py-2 bg-purple-500 rounded-lg text-sm font-medium"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default Upload;
